import { CommonModule, AsyncPipe } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, provideNativeDateAdapter } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDividerModule } from '@angular/material/divider';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { Medication } from 'fhir/r4';
import { ErrorService } from '../../shared/error.service';
import { UtilityService } from '../../shared/utility.service';
import { DosageDraftForm, NewMedicationDispenseService, PendingDispenseDraft, WalkInPatientPayload } from './new-medication-dispense.service';
import { DynamicFormsV2Component } from '../../shared/dynamic-forms-v2/dynamic-forms-v2.component';
import { formMetaData, FormFields, IndividualField } from '../../shared/dynamic-forms.interface2';

@Component({
    selector: 'app-new-medication-dispense',
    standalone: true,
    templateUrl: './new-medication-dispense.component.html',
    styleUrls: ['./new-medication-dispense.component.scss'],
    providers: [provideNativeDateAdapter()],
    imports: [
        CommonModule,
        AsyncPipe,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSelectModule,
        MatChipsModule,
        MatTooltipModule,
        MatAutocompleteModule,
        MatDividerModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatTooltipModule
    ]
})
export class NewMedicationDispenseComponent implements OnInit {
    private readonly fb = inject(FormBuilder);
    private readonly dialogRef = inject(MatDialogRef<NewMedicationDispenseComponent>);
    private readonly dialog = inject(MatDialog);
    private readonly dispenseService = inject(NewMedicationDispenseService);
    private readonly errorService = inject(ErrorService);
    private readonly snackBar = inject(MatSnackBar);
    protected readonly utilityService = inject(UtilityService);
    private readonly lowStockSnackKeys = new Set<string>();

    readonly patientForm = this.fb.group({
        name: ['', [Validators.required, Validators.minLength(3)]],
        birthDate: this.fb.control<Date | string | null>(null),
        gender: this.fb.control<string>('')
    });

    private readonly medicationsArray = this.fb.array<FormGroup>([]);

    readonly form = this.fb.group({
        patient: this.patientForm,
        medications: this.medicationsArray
    });

    readonly inventorySearchCtrl = new FormControl('', { nonNullable: true });
    inventoryResults$!: Observable<Medication[]>;
    inventoryLoading = false;
    submitting = false;
    selectedMedication: Medication | null = null;

    readonly genderOptions = [
        { label: 'Not specified', value: '' },
        { label: 'Female', value: 'female' },
        { label: 'Male', value: 'male' },
        { label: 'Other', value: 'other' },
        { label: 'Unknown', value: 'unknown' }
    ];

    ngOnInit(): void {
        this.inventoryResults$ = this.inventorySearchCtrl.valueChanges.pipe(
            startWith(''),
            debounceTime(300),
            distinctUntilChanged(),
            switchMap((value) => {
                if (value && typeof value === 'object') {
                    this.inventoryLoading = false;
                    return of([]);
                }
                this.inventoryLoading = true;
                const term = value ?? '';
                return this.dispenseService.searchMedications(term).pipe(
                    finalize(() => (this.inventoryLoading = false)),
                    catchError(() => {
                        this.inventoryLoading = false;
                        return of([]);
                    })
                );
            }),
            shareReplay(1)
        );
    }

    get medicationFormArray(): FormArray<FormGroup> {
        return this.medicationsArray;
    }

    get hasSelectedMedications(): boolean {
        return this.medicationFormArray.length > 0;
    }

    get totalQuantity(): number {
        return this.medicationFormArray.controls.reduce((acc, group) => {
            const qty = Number(group.get('quantity')?.value ?? 0);
            return acc + (Number.isFinite(qty) ? qty : 0);
        }, 0);
    }

    handleMedicationPick(medication: Medication): void {
        if (!medication?.id) {
            this.errorService.openandCloseError('Medication is missing an id.');
            return;
        }
        const alreadySelected = this.medicationFormArray.controls.some((group) => group.get('medicationId')?.value === medication.id);
        if (alreadySelected) {
            this.errorService.openandCloseError('Medication already added to this dispense.');
            return;
        }
        this.medicationFormArray.push(this.createMedicationGroup(medication));
    }

    adjustQuantity(index: number, delta: number): void {
        const group = this.medicationFormArray.at(index);
        if (!group) {
            return;
        }
        const control = group.get('quantity');
        if (!control) {
            return;
        }
        const currentValue = this.normalizeNumericInput(control.value);
        const base = currentValue && currentValue > 0 ? currentValue : 1;
        let next = base + delta;
        const max = this.getMedicationQuantityCap(group);
        if (max !== null) {
            next = Math.min(next, max);
        }
        next = Math.max(1, next);
        control.setValue(next);
        control.markAsDirty();
        control.markAsTouched();
    }

    removeMedication(index: number): void {
        this.medicationFormArray.removeAt(index);
    }

    trackMedication(index: number, group: FormGroup): string {
        return group.get('medicationId')?.value || String(index);
    }

    getMedicationTitle(medication: Medication | null | undefined): string {
        if (!medication) {
            return 'Select a medication';
        }
        return (
            medication.code?.text ||
            medication.code?.coding?.[0]?.display ||
            medication.code?.coding?.[0]?.code ||
            `Medication/${medication.id}`
        );
    }

    getMedicationSubtitle(medication: Medication | null | undefined): string {
        if (!medication) return '';
        const form = medication.form?.text || medication.form?.coding?.[0]?.display;
        const batch = medication.batch?.lotNumber ? `Lot ${medication.batch.lotNumber}` : '';
        return [form, batch].filter(Boolean).join(' • ');
    }

    getMedicationStockLabel(medication: Medication | null | undefined): string | null {
        if (!medication) return null;
        const remaining = this.utilityService.getMedicationTotalRemaining(medication);
        if (!remaining) {
            return null;
        }
        const unit = remaining.unit ? ` ${remaining.unit}` : '';
        const config = this.utilityService.getMedicationLowStockConfig(medication);
        const isLow = this.utilityService.isBelowLowStockThreshold(remaining.value, config?.threshold);
        return `${remaining.value}${unit} in stock${isLow ? ' (low stock)' : ''}`;
    }
    getMedicationTotalRemainingFromStockLabel(label: string | null): number | null {
        if (!label) return null;
        const match = label.match(/^(\d+(\.\d+)?)/);
        if (match) {
            return parseFloat(match[1]);
        }
        return null;
    }

    getMedicationQuantityCap(group: FormGroup): number | null {
        const medication = group.get('medication')?.value as Medication | null;
        return this.getMedicationTotalRemainingFromStockLabel(this.getMedicationStockLabel(medication));
    }

    getQuantityValue(group: FormGroup): number {
        const control = group.get('quantity');
        const value = this.normalizeNumericInput(control?.value);
        return value !== null && value > 0 ? value : 0;
    }
    clearSearch(): void {
        this.inventorySearchCtrl.setValue('');
        this.selectedMedication = null;
    }

    displayMedication = (medication: Medication | null): string => {
        if (!medication) return '';
        return this.getMedicationTitle(medication);
    };

    handleMedicationSelected(medication: Medication): void {
        this.selectedMedication = medication ?? null;
    }

    addSelectedMedication(): void {
        if (!this.selectedMedication) {
            this.errorService.openandCloseError('Select a medication first.');
            return;
        }
        this.handleMedicationPick(this.selectedMedication);
        this.selectedMedication = null;
        this.inventorySearchCtrl.setValue('');
    }

    openDosageDialog(group: FormGroup): void {
        const dialogRef = this.dialog.open(DynamicFormsV2Component, {
            width: '640px',
            maxHeight: '90vh',
            data: {
                formMetaData: this.buildDosageFormMeta(),
                formFields: this.buildDosageFormFields(),
                prepopulatedData: this.extractDosageFormValues(group)
            }
        });

        dialogRef.afterClosed().subscribe((result) => {
            const values = result?.values;
            if (!values) {
                return;
            }
            this.applyDosageFormValues(group, values);
        });
    }

    hasDosage(group: FormGroup): boolean {
        return this.extractDosageText(group).length > 0;
    }

    getDosageSummary(group: FormGroup): string {
        const text = this.extractDosageText(group);
        if (!text) {
            return 'Dosage not captured yet';
        }
        return text.length > 70 ? `${text.slice(0, 67)}…` : text;
    }

    private extractDosageText(group: FormGroup): string {
        return (group.get('dosageInstruction')?.value ?? '').toString().trim();
    }

    private buildDosageFormFields(): FormFields[] {
        return [
            this.buildDosageInstructionField(),
            ...this.getReusableDosageFields()
        ];
    }

    private buildDosageInstructionField(): IndividualField {
        return {
            generalProperties: {
                fieldApiName: 'dosageInstruction',
                fieldName: 'Dosage Instruction',
                fieldLabel: 'Dosage Instruction',
                fieldHint: 'Describe how the patient should take this medication. This text becomes the dosage summary.',
                inputType: 'textarea',
                fieldType: 'IndividualField',
                isArray: false,
                isGroup: false,
                validations: [
                    {
                        type: 'default',
                        name: 'required',
                        isFunction: false
                    }
                ]
            },
            data: '',
            dataType: 'string'
        };
    }

    private getReusableDosageFields(): FormFields[] {
        const raw = this.utilityService.commonFormFields['dosage'];
        const normalized = Array.isArray(raw) ? raw : raw ? [raw] : [];
        return this.deepClone(normalized);
    }

    private buildDosageFormMeta(): formMetaData {
        return {
            formName: 'Dispense Dosage',
            formDescription: 'Capture dosage instructions plus timing and quantity details. Duration combines with its unit to describe the full course.',
            submitText: 'Save Dosage',
            closeDialogOnSubmit: true
        };
    }

    private extractDosageFormValues(group: FormGroup) {
        const timingGroup = group.get('dosageTiming') as FormGroup | null;
        const doseGroup = group.get('doseAndRateDoseQuantity') as FormGroup | null;
        return {
            dosageInstruction: group.get('dosageInstruction')?.value,
            dosageTiming: timingGroup?.value,
            doseAndRateDoseQuantity: doseGroup?.value
        };
    }

    private applyDosageFormValues(group: FormGroup, values: any): void {
        group.get('dosageInstruction')?.setValue(values?.dosageInstruction ?? '');

        const timingGroup = group.get('dosageTiming') as FormGroup | null;
        if (timingGroup) {
            if (values?.dosageTiming) {
                timingGroup.patchValue(values.dosageTiming);
            } else {
                timingGroup.reset();
            }
        }

        const doseGroup = group.get('doseAndRateDoseQuantity') as FormGroup | null;
        if (doseGroup) {
            if (values?.doseAndRateDoseQuantity) {
                doseGroup.patchValue(values.doseAndRateDoseQuantity);
            } else {
                doseGroup.reset();
            }
        }

        group.markAsDirty();
        group.updateValueAndValidity();
    }

    private deepClone<T>(value: T): T {
        if (value === null || value === undefined) {
            return value;
        }
        const globalAny: any = globalThis as any;
        if (typeof globalAny.structuredClone === 'function') {
            return globalAny.structuredClone(value);
        }
        return JSON.parse(JSON.stringify(value));
    }

    submit(): void {
        if (!this.hasSelectedMedications) {
            this.errorService.openandCloseError('Add at least one medication.');
            return;
        }
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.errorService.openandCloseError('Complete the highlighted fields.');
            return;
        }

        const patientPayload = this.buildPatientPayload();
        const rows: PendingDispenseDraft[] = this.medicationFormArray.controls.map((group) => this.buildDraftFromGroup(group));
        console.log('[tracing dispense submission]', patientPayload, rows);
        this.submitting = true;
        this.dispenseService.submitDispenses(patientPayload, rows).pipe(finalize(() => (this.submitting = false))).subscribe({
            next: () => {
                this.snackBar.open('Dispense submitted for pharmacy fulfilment.', 'Dismiss', { duration: 4000 });
                this.dialogRef.close(true);
            },
            error: (error) => {
                const message = error?.message || 'Unable to submit dispense. Please try again.';
                this.errorService.openandCloseError(message);
            }
        });
    }

    cancel(): void {
        this.dialogRef.close(false);
    }

    private createMedicationGroup(medication?: Medication): FormGroup {
        return this.fb.group({
            medication: [medication ?? null, Validators.required],
            medicationId: [medication?.id ?? '', Validators.required],
            quantity: [1, [Validators.required, Validators.min(1)]],
            dosageInstruction: ['', Validators.required],
            dosageTiming: this.fb.group({
                timingCode: [''],
                duration: [''],
                durationUnit: ['']
            }),
            doseAndRateDoseQuantity: this.fb.group({
                value: [''],
                unit: ['']
            })
        });
    }

    private buildPatientPayload(): WalkInPatientPayload {
        const raw = this.patientForm.value;
        return {
            name: (raw.name ?? '').trim(),
            birthDate: this.formatDate(raw.birthDate),
            gender: (raw.gender ?? '') as WalkInPatientPayload['gender']
        };
    }

    private buildDraftFromGroup(group: FormGroup): PendingDispenseDraft {
        console.log('[tracing dispense quantity]', group.get('quantity')?.value, group.value);
        const medication = group.get('medication')?.value as Medication | null;
        const medicationId = group.get('medicationId')?.value;
        if (!medication || !medicationId) {
            throw new Error('Medication details are incomplete.');
        }
        const quantity = this.normalizeNumericInput(group.get('quantity')?.value);
        console.log('[tracing dispense quantity]', quantity, 'after normalization');
        if (!quantity || quantity <= 0) {
            throw new Error('Quantity must be greater than zero.');
        }
        this.warnIfLowStock(medication, quantity);
        return {
            id: medicationId,
            medication,
            quantity,
            dosageDraft: this.buildDosageDraft(group)
        };
    }

    private buildDosageDraft(group: FormGroup): DosageDraftForm {
        const instruction = (group.get('dosageInstruction')?.value ?? '').toString().trim();
        const timingGroup = group.get('dosageTiming') as FormGroup | null;
        const doseGroup = group.get('doseAndRateDoseQuantity') as FormGroup | null;

        const sanitizedTiming = timingGroup
            ? this.utilityService.stripEmptyValues<DosageDraftForm['dosageTiming']>(timingGroup.value)
            : undefined;
        const sanitizedDose = doseGroup
            ? this.utilityService.stripEmptyValues<DosageDraftForm['doseAndRateDoseQuantity']>(doseGroup.value)
            : undefined;

        return {
            ...(instruction ? { dosageInstruction: instruction } : {}),
            ...(sanitizedTiming ? { dosageTiming: sanitizedTiming } : {}),
            ...(sanitizedDose ? { doseAndRateDoseQuantity: sanitizedDose } : {})
        };
    }

    private formatDate(input: Date | string | null | undefined): string | undefined {
        if (!input) {
            return undefined;
        }
        if (typeof input === 'string') {
            return input;
        }
        const year = input.getFullYear();
        const month = `${input.getMonth() + 1}`.padStart(2, '0');
        const day = `${input.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private normalizeNumericInput(input: any): number | null {
        if (input === null || input === undefined || input === '') {
            return null;
        }
        const value = Number(input);
        return Number.isFinite(value) ? value : null;
    }

    private warnIfLowStock(medication: Medication, quantity: number): void {
        const remainingInfo = this.utilityService.getMedicationTotalRemaining(medication);
        const config = this.utilityService.getMedicationLowStockConfig(medication);
        if (!config?.threshold || !remainingInfo || remainingInfo.value === undefined || remainingInfo.value === null) {
            return;
        }

        const remainingAfter = remainingInfo.value - quantity;
        if (!this.utilityService.isBelowLowStockThreshold(remainingAfter, config.threshold)) {
            return;
        }

        const key = `${medication.id}-${config.threshold}`;
        if (this.lowStockSnackKeys.has(key)) {
            return;
        }
        this.lowStockSnackKeys.add(key);

        const formatter = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
        const medLabel = this.getMedicationTitle(medication);
        const unit = remainingInfo.unit || config.unit || '';
        const remainingText = formatter.format(Math.max(0, remainingAfter));
        const thresholdText = formatter.format(Math.max(0, config.threshold));

        this.snackBar.open(
            `Low stock warning: ${medLabel} will drop to ${remainingText}${unit ? ` ${unit}` : ''} (threshold ${thresholdText}${unit ? ` ${unit}` : ''}).`,
            'Dismiss',
            { duration: 6000 }
        );
    }
}
